import { offlineService, PendingActionStatus } from './offlineDb';
import { supabase } from './supabase';

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.listeners = new Set();
    this.setupOnlineListener();
  }

  setupOnlineListener() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        console.log('[Sync] Connection restored, starting sync...');
        this.syncAll();
      });
    }
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify(event) {
    this.listeners.forEach(callback => callback(event));
  }

  isOnline() {
    return typeof navigator !== 'undefined' ? navigator.onLine : true;
  }

  async queueAction(action) {
    const id = await offlineService.addPendingAction(action);
    this.notify({ type: 'queued', actionId: id });

    if (this.isOnline()) {
      setTimeout(() => this.syncAll(), 100);
    } else {
      this.registerBackgroundSync().catch(() => {});
    }

    return id;
  }

  async syncAll() {
    if (this.isSyncing || !this.isOnline()) {
      return {
        success: false,
        reason: this.isSyncing ? 'already_syncing' : 'offline',
      };
    }

    this.isSyncing = true;
    this.notify({ type: 'sync_started' });

    try {
      const pendingActions = await offlineService.getPendingActions();

      if (pendingActions.length === 0) {
        this.notify({ type: 'sync_completed', synced: 0 });
        return { success: true, synced: 0 };
      }

      console.log(
        `[Sync] Processing ${pendingActions.length} pending actions...`
      );

      let syncedCount = 0;
      let failedCount = 0;

      for (const action of pendingActions) {
        if (!this.isOnline()) {
          console.log('[Sync] Lost connection, stopping sync');
          break;
        }

        if (action.retries >= MAX_RETRIES) {
          console.log(
            `[Sync] Action ${action.id} exceeded max retries, skipping`
          );
          failedCount++;
          continue;
        }

        try {
          await offlineService.updateActionStatus(
            action.id,
            PendingActionStatus.SYNCING
          );

          const payload = JSON.parse(action.payload);
          const response = await this.executeAction(action, payload);

          if (response.ok) {
            await offlineService.updateActionStatus(
              action.id,
              PendingActionStatus.COMPLETED
            );
            syncedCount++;
            this.notify({ type: 'action_synced', actionId: action.id });
          } else {
            throw new Error(response.error || 'Sync failed');
          }
        } catch (error) {
          console.error(`[Sync] Failed to sync action ${action.id}:`, error);
          await offlineService.updateActionStatus(
            action.id,
            PendingActionStatus.FAILED,
            error.message
          );
          failedCount++;
          this.notify({
            type: 'action_failed',
            actionId: action.id,
            error: error.message,
          });

          await this.delay(RETRY_DELAY_MS);
        }
      }

      await offlineService.clearCompletedActions();

      this.notify({
        type: 'sync_completed',
        synced: syncedCount,
        failed: failedCount,
      });

      return { success: true, synced: syncedCount, failed: failedCount };
    } catch (error) {
      console.error('[Sync] Sync failed:', error);
      this.notify({ type: 'sync_error', error: error.message });
      return { success: false, error: error.message };
    } finally {
      this.isSyncing = false;
    }
  }

  async executeAction(action, payload) {
    const { endpoint, method } = action;

    if (action.transport === 'supabase_rpc') {
      if (!action.rpc) {
        return { ok: false, error: 'Missing Supabase RPC name' };
      }

      try {
        const { data, error } = await supabase.rpc(action.rpc, payload);
        if (error) return { ok: false, error: error.message };
        return { ok: true, data };
      } catch (error) {
        return { ok: false, error: error.message };
      }
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    try {
      const response = await fetch(endpoint, {
        method: method || 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          ok: false,
          error: errorData.message || `HTTP ${response.status}`,
        };
      }

      const data = await response.json().catch(() => ({}));
      return { ok: true, data };
    } catch (error) {
      return { ok: false, error: error.message };
    }
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getPendingCount() {
    return await offlineService.getPendingCount();
  }

  async registerBackgroundSync() {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('nova-crm-sync');
        console.log('[Sync] Background sync registered');
        return true;
      } catch (error) {
        console.warn('[Sync] Background sync not available:', error);
        return false;
      }
    }
    return false;
  }
}

export const syncService = new SyncService();
export default syncService;
